import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchData } from "../../redux/slices/mainDataSlice";
import toast from "react-hot-toast";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faPlus,
  faEdit,
  faTrashAlt,
  faSearch,
  faCheckCircle,
  faHeading,
  faCalendarCheck,
  faUmbrellaBeach,
  faBookOpen,
  faDoorOpen,
  faCalendar,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import "../../main.scss";
import Can from "../../permissions/Can";
import Tooltip from "../../components/Tooltip";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const getDayName = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("ar-EG", { weekday: "long" });
};

const isToday = (dateString) => {
  if (!dateString) return false;
  const today = new Date();
  const eventDate = new Date(dateString);
  return (
    today.getDate() === eventDate.getDate() &&
    today.getMonth() === eventDate.getMonth() &&
    today.getFullYear() === eventDate.getFullYear()
  );
};

const getEventCategory = (eventName) => {
  const text = eventName?.toLowerCase() || "";
  if (text.includes("عطلة")) {
    return {
      key: "holiday",
      label: "عطلة",
      color: "#ef4444",
      bgColor: "rgba(239, 68, 68, 0.1)",
      icon: faUmbrellaBeach,
    };
  }
  if (text.includes("امتحان")) {
    return {
      key: "exam",
      label: "امتحان",
      color: "#f59e0b",
      bgColor: "rgba(245, 158, 11, 0.1)",
      icon: faBookOpen,
    };
  }
  if (text.includes("دوام")) {
    return {
      key: "attendance",
      label: "دوام",
      color: "#10b981",
      bgColor: "rgba(16, 185, 129, 0.1)",
      icon: faDoorOpen,
    };
  }
  return {
    key: "other",
    label: "حدث",
    color: "#ec4899",
    bgColor: "rgba(236, 72, 153, 0.1)",
    icon: faCalendar,
  };
};

const getAcademicTerm = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  if (month >= 9 || month === 1) {
    return "الفصل الدراسي الأول";
  }
  return "الفصل الدراسي الثاني";
};

const AnnualEvents = () => {
  const dispatch = useDispatch();
  const events = useSelector(
    (state) => state.main.data?.annualEventsData || []
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [animateIn, setAnimateIn] = useState(false);

  const [newEvent, setNewEvent] = useState({ Mnwasf: "", Fulldays: "" });
  const [editEvent, setEditEvent] = useState({
    Mn_ID: null,
    Mnwasf: "",
    Fulldays: "",
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await dispatch(fetchData());
      setLoading(false);
    };
    load();
  }, [dispatch]);

  useEffect(() => {
    if (showAddModal || showEditModal) {
      setTimeout(() => setAnimateIn(true), 10);
    } else {
      setAnimateIn(false);
    }
  }, [showAddModal, showEditModal]);

  const filteredEvents = useMemo(() => {
    return events
      .filter((e) => e.Mnwasf?.toLowerCase().includes(filter.toLowerCase()))
      .sort((a, b) => new Date(a.Fulldays) - new Date(b.Fulldays));
  }, [events, filter]);

  const stats = useMemo(() => {
    return {
      total: filteredEvents.length,
      holiday: filteredEvents.filter(e => getEventCategory(e.Mnwasf).key === "holiday").length,
      exam: filteredEvents.filter(e => getEventCategory(e.Mnwasf).key === "exam").length,
      attendance: filteredEvents.filter(e => getEventCategory(e.Mnwasf).key === "attendance").length,
    };
  }, [filteredEvents]);

  const groupedEvents = useMemo(() => {
    return filteredEvents.reduce((acc, event) => {
      const term = getAcademicTerm(event.Fulldays);
      if (!acc[term]) acc[term] = [];
      acc[term].push(event);
      return acc;
    }, {});
  }, [filteredEvents]);

  const toggleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) return;

    toast(
      (t) => (
        <div className="confirm-toast">
          <p
            style={{ fontWeight: 400, fontSize: "16px", marginBottom: "10px" }}
          >
            تأكيد الحذف الجماعي
          </p>
          <p>هل أنت متأكد من حذف ({selectedItems.length}) مناسبة محددة؟</p>
          <div className="toast-actions">
            <button
              onClick={async () => {
                const loadingT = toast.loading("جارٍ الحذف...");
                try {
                  const credentials = JSON.parse(
                    localStorage.getItem("userCredentials")
                  );
                  const token = credentials?.token;
                  await Promise.all(
                    selectedItems.map((id) =>
                      axios.delete(
                        `https://api.alfares.cloud/api/alfares/annual-events/${id}/${token}`
                      )
                    )
                  );
                  toast.success("تم الحذف بنجاح", { id: loadingT });
                  dispatch(fetchData());
                  setSelectedItems([]);
                } catch {
                  toast.error("فشل في حذف بعض العناصر", { id: loadingT });
                } finally {
                  toast.dismiss(t.id);
                }
              }}
              className="btn-confirm"
              style={{ background: "#ef4444" }}
            >
              نعم، حذف
            </button>
            <button onClick={() => toast.dismiss(t.id)} className="btn-cancel">
              إلغاء
            </button>
          </div>
        </div>
      ),
      { duration: 8000 }
    );
  };



  const handleAdd = async () => {
    if (!newEvent.Mnwasf || !newEvent.Fulldays)
      return toast.error("أكمل البيانات");
    try {
      const credentials = JSON.parse(localStorage.getItem("userCredentials"));
      await axios.post(
        `https://api.alfares.cloud/api/alfares/annual-events/${credentials?.token}`,
        newEvent
      );
      setNewEvent({ Mnwasf: "", Fulldays: "" });
      setShowAddModal(false);
      toast.success("تمت الإضافة");
      dispatch(fetchData());
    } catch {
      toast.error("فشل الإضافة");
    }
  };

  const handleUpdate = async () => {
    if (!editEvent.Mnwasf || !editEvent.Fulldays)
      return toast.error("أكمل البيانات");
    try {
      const credentials = JSON.parse(localStorage.getItem("userCredentials"));
      await axios.put(
        `https://api.alfares.cloud/api/alfares/annual-events/${editEvent.Mn_ID}/${credentials?.token}`,
        {
          Mnwasf: editEvent.Mnwasf,
          Fulldays: editEvent.Fulldays,
        }
      );
      setShowEditModal(false);
      toast.success("تم التعديل");
      dispatch(fetchData());
    } catch {
      toast.error("فشل التعديل");
    }
  };

  return (
    <div className="events-wrapper anim-fade-in" style={{ padding: "5px" }}>
      <div className="premium-edit-container">
        {/* Premium Header */}
        <div className="premium-header-modern">
          <div className="header-left">
            <div className="header-icon-box" style={{ background: "linear-gradient(135deg, #ec4899, #be185d)" }}>
              <FontAwesomeIcon icon={faCalendarCheck} />
            </div>
            <div className="header-text-box">
              <h2>المناسبات والفعاليات السنوية</h2>
              <div className="breadcrumb-info">
                <span>نظام الفارس</span>
                <span className="separator">/</span>
                <span className="current-page">سجل المناسبات الرسمي</span>
              </div>
            </div>
          </div>

          <div className="header-buttons-group">
            <div className="global-search-container">
              <FontAwesomeIcon icon={faSearch} className="s-icon" />
              <input
                type="text"
                placeholder="ابحث عن مناسبة..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>

            <div className="results-count">
              <b>{filteredEvents.length}</b> مناسبة
            </div>

            <Can module="holidays" action="delete">
              <button
                className={`bulk-delete-btn ${
                  selectedItems.length > 0 ? "show" : ""
                }`}
                onClick={handleDeleteSelected}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
                <span>حذف المحدد ({selectedItems.length})</span>
              </button>
            </Can>

            {/* <button
              onClick={handleUpdateEventsToNextYear}
              className="premium-action-btn update-btn"
            >
              <FontAwesomeIcon icon={faSyncAlt} />
              <span>تحديث للعام القادم</span>
            </button> */}

            <Can module="holidays" action="add">
              <button
                onClick={() => setShowAddModal(true)}
                className="premium-action-btn"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>إضافة مناسبة</span>
              </button>
            </Can>
          </div>
        </div>

        <div className="premium-table-container">
          {loading ? (
            <div className="loader-overlay">
              <div className="premium-loader"></div>
              <span>جارٍ جلب السجلات...</span>
            </div>
          ) : filteredEvents.length > 0 ? (
            <>
              {/* Stats Row */}
              <div className="stats-row">
                <div className="stat-card">
                  <span className="stat-label">الإجمالي</span>
                  <span className="stat-value">{stats.total}</span>
                </div>
                <div className="stat-card holiday-stat">
                  <span className="stat-label">عطلة</span>
                  <span className="stat-value">{stats.holiday}</span>
                </div>
                <div className="stat-card exam-stat">
                  <span className="stat-label">امتحان</span>
                  <span className="stat-value">{stats.exam}</span>
                </div>
                <div className="stat-card attendance-stat">
                  <span className="stat-label">دوام</span>
                  <span className="stat-value">{stats.attendance}</span>
                </div>
              </div>

              {/* Grouped Events */}
              {Object.entries(groupedEvents).map(([term, termEvents]) => (
                <div key={term} className="term-group">
                  <div className="term-header">{term}</div>
                  <div className="holidays-modern-grid">
                    {termEvents.map((item) => {
                      const isSelected = selectedItems.includes(item.Mn_ID);
                      const isTodayEvent = isToday(item.Fulldays);
                      const eventDate = new Date(item.Fulldays);
                      const day = eventDate.getDate();
                      const dayName = getDayName(item.Fulldays);
                      const category = getEventCategory(item.Mnwasf);

                      return (
                        <div
                          key={item.Mn_ID}
                          className={`holiday-card-modern ${
                            isTodayEvent ? "is-today" : ""
                          } ${isSelected ? "is-selected" : ""}`}
                          onClick={() => {
                            setEditEvent(item);
                            setShowEditModal(true);
                          }}
                        >
                          {/* Checkbox for selection */}
                          <div
                            className="card-checkbox-wrapper"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSelectItem(item.Mn_ID);
                            }}
                          >
                            <input type="checkbox" checked={isSelected} readOnly />
                          </div>

                          {/* Calendar Icon Style Date */}
                          <div className="holiday-calendar-icon">
                            <div
                              className="calendar-top"
                              style={{ background: category.color }}
                            >
                              <FontAwesomeIcon icon={category.icon} />
                            </div>
                            <div className="calendar-body">
                              <span className="day-num">{day}</span>
                              <span className="day-name">{dayName}</span>
                            </div>
                          </div>

                          {/* Event Info */}
                          <div className="holiday-content-main">
                            <div className="holiday-title-row">
                              <h3 className="holiday-name">
                                {item.Mnwasf || "بدون اسم"}
                              </h3>
                              {isTodayEvent && (
                                <span className="today-badge">اليوم</span>
                              )}
                            </div>
                            <div className="holiday-full-date">
                              <FontAwesomeIcon
                                icon={faCalendarAlt}
                                className="date-icon"
                              />
                              <span>{formatDate(item.Fulldays)}</span>
                            </div>
                          </div>

                          {/* Quick Actions - Always Visible */}
                          <div className="holiday-card-actions">
                            <Tooltip content="تعديل">
                              <button
                                className="quick-action-btn edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditEvent(item);
                                  setShowEditModal(true);
                                }}
                             
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="empty-ui-state">
              <FontAwesomeIcon
                icon={faCalendarCheck}
                className="icon"
                style={{ color: "#ec4899" }}
              />
              <h3>لا توجد مناسبات مضافة</h3>
              <p>ابدأ بترتيب جدول فعالياتك ومناسباتك لمزيد من التنظيم.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {(showAddModal || showEditModal) && (
        <div className={`premium-modal-overlay ${animateIn ? "active" : ""}`}>
          <div className="premium-modal-card events-modal-card">
            <div className="modal-header events-modal-header">
              <div className="events-modal-header-inner">
                <div className="events-modal-icon-box">
                  <FontAwesomeIcon
                    icon={showAddModal ? faPlus : faEdit}
                    className="events-modal-icon"
                  />
                </div>
                <h3>
                  {showAddModal ? "إضافة مناسبة جديدة" : "تحديث بيانات المناسبة"}
                </h3>
              </div>
              <button
                type="button"
                className="events-modal-close-btn"
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                }}
                aria-label="إغلاق"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body events-modal-body">
              <div className="events-input-field">
                <label>
                  <span className="req-asterisk">*</span>
                  <span>اسم المناسبة / الفعالية</span>
                </label>
                <div className="events-input-wrapper">
                  <FontAwesomeIcon icon={faHeading} className="events-field-icon" />
                  <input
                    value={showAddModal ? newEvent.Mnwasf : editEvent.Mnwasf}
                    onChange={(e) =>
                      showAddModal
                        ? setNewEvent({ ...newEvent, Mnwasf: e.target.value })
                        : setEditEvent({ ...editEvent, Mnwasf: e.target.value })
                    }
                    placeholder="مثال: عيد الاستقلال، بداية عطلة الشتاء"
                    className="events-modal-input"
                  />
                </div>
              </div>

              <div className="events-input-field">
                <label>
                  <span className="req-asterisk">*</span>
                  <span>تاريخ اليوم المحدد</span>
                </label>
                <div className="events-input-wrapper">
                  <input
                    type="date"
                    value={
                      showAddModal
                        ? newEvent.Fulldays
                        : editEvent.Fulldays
                        ? new Date(editEvent.Fulldays)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      showAddModal
                        ? setNewEvent({ ...newEvent, Fulldays: e.target.value })
                        : setEditEvent({
                            ...editEvent,
                            Fulldays: e.target.value,
                          })
                    }
                    className="events-modal-input events-date-input"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer events-modal-footer">
              <button
                className="events-modal-submit"
                onClick={showAddModal ? handleAdd : handleUpdate}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>{showAddModal ? "إضافة المناسبة" : "حفظ التغييرات"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .events-wrapper { padding: 5px; color: var(--text); font-family: 'Cairo', sans-serif; }
        
        .premium-edit-container { 
          margin: 0;
          background: transparent;
          border: none;
          border-radius: 0;
          overflow: visible;
          box-shadow: none;
          width: 100%;
        }
        
        .premium-header-modern {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 30px;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-radius: 24px 24px 0 0;
          direction: rtl;
          gap: 20px;
        }

        .header-left { 
          display: flex; 
          align-items: center; 
          gap: 12px; 
        }
        
        .header-icon-box {
          width: 32px; height: 32px;
          background: linear-gradient(135deg, #10b981, #059669);
          border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; color: white;
          box-shadow: 0 4px 10px -2px rgba(16, 185, 129, 0.4);
        }
        
        .header-text-box h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 400;
          color: var(--text);
          white-space: nowrap;
        }
        .breadcrumb-info { display: flex; align-items: center; gap: 4px; color: var(--muted); font-size: 10px; margin-top: 1px; }
        .current-page { color: #ec4899; font-weight: 400; }

        .header-buttons-group {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: nowrap;
        }
                
                .header-left-side {
                    width: 100%;
                }

                .global-search-container {
                    background: var(--panel-2); border: 1px solid var(--border); border-radius: 10px;
                    padding: 0 15px; display: flex; align-items: center; gap: 12px; 
                    width: 280px; height: 44px;
                    transition: 0.3s; box-sizing: border-box;
                }
                .global-search-container:focus-within {
                    border-color: #ec4899; background: var(--bg); box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.1);
                }
                .global-search-container input { background: transparent; border: none; color: var(--text); font-size: 12px; width: 100%; outline: none; font-family: 'Cairo', sans-serif; }
                .results-count {
                    background: var(--panel-2);
                    padding: 0 15px;
                    border-radius: 12px;
                    font-size: 13px;
                    color: var(--muted);
                    border: 1px solid var(--border);
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    height: 44px;
                    white-space: nowrap;
                    transition: all 0.3s;
                }
                .results-count b { color: #10b981; }

                .premium-action-btn {
                    padding: 6px 14px; font-size: 12px; font-weight: 400; border-radius: 8px;
                    gap: 6px; display: flex; align-items: center; cursor: pointer; transition: 0.3s;
                    border: none; font-family: 'Cairo', sans-serif; color: white; white-space: nowrap;
                    background: #10b981;
                }
                .premium-action-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 12px -3px rgba(16, 185, 129, 0.3); }
                .update-btn { background: var(--panel-2); color: var(--text); border: 1px solid var(--border); }
                .update-btn:hover { background: #10b981; color: white; border-color: #10b981; }

                .bulk-delete-btn {
                    background: #ef4444; color: white; border: none; padding: 6px 14px; border-radius: 8px;
                    font-weight: 400; display: none; align-items: center; gap: 6px; font-family: 'Cairo', sans-serif;
                    transition: 0.3s; font-size: 12px; cursor: pointer; white-space: nowrap;
                }
                .bulk-delete-btn.show { display: flex; }

        .premium-table-container {
          padding: 25px;
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-top: none;
          border-radius: 0 0 24px 24px;
        }

        .stats-row {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-bottom: 20px;
          direction: rtl;
        }

        .stat-card {
          background: var(--panel-2);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 12px 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          transition: all 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px -3px rgba(0, 0, 0, 0.2);
        }

        .stat-label {
          font-size: 11px;
          color: var(--muted);
          font-weight: 400;
        }

        .stat-value {
          font-size: 18px;
          font-weight: 400;
          color: var(--text);
        }

        .stat-card.holiday-stat .stat-value { color: #ef4444; }
        .stat-card.exam-stat .stat-value { color: #f59e0b; }
        .stat-card.attendance-stat .stat-value { color: #10b981; }

        .term-group {
          margin-bottom: 25px;
        }

        .term-group:last-child {
          margin-bottom: 0;
        }

        .term-header {
          font-size: 14px;
          font-weight: 400;
          color: var(--muted);
          margin-bottom: 12px;
          padding: 8px 12px;
          background: var(--panel-2);
          border-radius: 8px;
          border-left: 3px solid #10b981;
          direction: rtl;
        }
                
                /* Modern Grid & Cards */
                .holidays-modern-grid {
                    display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 15px; direction: rtl;
                }

                .holiday-card-modern {
                    background: var(--panel-2); border: 1px solid var(--border); border-radius: 16px;
                    padding: 15px; display: flex; align-items: flex-start; gap: 15px; position: relative;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); cursor: pointer; overflow: hidden;
                }

                .holiday-card-modern:hover {
                    transform: translateY(-3px); border-color: #10b981;
                    box-shadow: 0 10px 20px -8px rgba(0,0,0,0.4); background: var(--panel);
                }

                .holiday-card-modern.is-today {
                    border-color: #10b981; background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, transparent 100%);
                }
                .holiday-card-modern.is-today::after {
                    content: ''; position: absolute; top: 0; left: 0; width: 3px; height: 100%; background: #10b981;
                }

                .holiday-card-modern.is-selected {
                    border-color: #10b981; background: rgba(16, 185, 129, 0.1);
                }

                .card-checkbox-wrapper { position: absolute; top: 12px; left: 12px; z-index: 5; }
                .card-checkbox-wrapper input { width: 16px; height: 16px; cursor: pointer; }

                .holiday-calendar-icon {
                    width: 55px; height: 65px; background: var(--panel); border: 1px solid var(--border);
                    border-radius: 10px; overflow: hidden; display: flex; flex-direction: column; flex-shrink: 0;
                    box-shadow: 0 3px 6px rgba(0,0,0,0.1);
                }

                .calendar-top { color: white; font-size: 12px; font-weight: 400; text-align: center; padding: 4px 0; text-transform: uppercase; display: flex; align-items: center; justify-content: center; }
                .calendar-body { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; background: white; color: #1e293b; }
                .day-num { font-size: 20px; font-weight: 400; line-height: 1; }
                .day-name { font-size: 9px; font-weight: 400; margin-top: 1px; opacity: 0.7; }

                .holiday-content-main { flex: 1; }
                .holiday-title-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; flex-wrap: wrap; }
                .holiday-name { margin: 0; font-size: 15px; font-weight: 400; color: var(--text); flex: 1; }
                .today-badge { color: white; font-size: 9px; font-weight: 400; padding: 1px 6px; border-radius: 4px; background: #10b981; }

                .holiday-full-date { display: flex; align-items: center; gap: 6px; color: var(--muted); font-size: 12px; font-weight: 400; }
                .date-icon { font-size: 12px; color: #10b981; }

                .holiday-card-actions {
                    position: absolute; bottom: 12px; left: 12px; opacity: 1; transform: translateX(0); transition: 0.3s;
                }

                .quick-action-btn {
                    width: 28px; height: 28px; border-radius: 6px; border: none;
                    background: var(--panel-2); color: var(--muted); cursor: pointer;
                    display: flex; align-items: center; justify-content: center; transition: 0.2s;
                    font-size: 12px;
                }
                .quick-action-btn:hover { background: #10b981; color: white; }

                .loader-overlay { padding: 40px; text-align: center; color: var(--muted); display: flex; flex-direction: column; gap: 15px; }
                .premium-loader { width: 32px; height: 32px; border: 3px solid rgba(16, 185, 129, 0.1); border-top-color: #10b981; border-radius: 50%; animation: spin 1s infinite linear; margin: 0 auto; }
                
                .empty-ui-state { text-align: center; padding: 60px 20px !important; }
                .empty-ui-state .icon { font-size: 50px; opacity: 0.2; margin-bottom: 20px; }
                .empty-ui-state h3 { font-size: 18px; font-weight: 400; color: var(--text); margin-bottom: 8px; }
                .empty-ui-state p { font-size: 14px; color: var(--muted); }

                /* Modal */
                .premium-modal-overlay {
                    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
                    background: rgba(13, 35, 32, 0.4); backdrop-filter: blur(6px);
                    display: flex; align-items: center; justify-content: center; z-index: 10000;
                    opacity: 0; pointer-events: none; transition: 0.3s;
                }
                .premium-modal-overlay.active { opacity: 1; pointer-events: auto; }
                .premium-modal-overlay.active .premium-modal-card { transform: translateY(0) scale(1); }

                .events-modal-card {
                    background: #FFFFFF;
                    border: 1px solid #e0e2e6;
                    width: 100%;
                    max-width: 380px;
                    border-radius: 16px;
                    padding: 0;
                    overflow: hidden;
                    transform: translateY(20px) scale(0.97);
                    transition: 0.3s;
                    box-shadow: 0 24px 60px -16px rgba(0,0,0,0.35);
                    font-family: 'Cairo', sans-serif;
                    direction: rtl;
                }

                .events-modal-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 16px 24px;
                    background: #FFFFFF;
                    border-bottom: 1px solid #f1f5f9;
                }
                .events-modal-header-inner {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .events-modal-icon-box {
                    width: 32px;
                    height: 32px;
                    background: rgba(16, 185, 129, 0.1);
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .events-modal-icon {
                    font-size: 16px;
                    color: #10b981;
                }
                .events-modal-header h3 {
                    margin: 0;
                    font-size: 15px;
                    font-weight: 400;
                    color: #181d26;
                    line-height: 1.4;
                }
                .events-modal-close-btn {
                    width: auto;
                    min-width: 0;
                    padding: 6px;
                    border-radius: 8px;
                    border: none;
                    background: transparent;
                    color: #6b7280;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    transition: 0.2s;
                }
                .events-modal-close-btn:hover {
                    background: #f1f5f9;
                    color: #181d26;
                }

                .events-modal-body {
                    padding: 20px 24px;
                    background: #F8FAFC;
                }

                .events-input-field {
                    margin-bottom: 16px;
                }
                .events-input-field:last-child {
                    margin-bottom: 0;
                }
                .events-input-field label {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 12px;
                    font-weight: 400;
                    color: #4B5563;
                    margin-bottom: 8px;
                    line-height: 1.4;
                }
                .req-asterisk {
                    color: #ef4444;
                    font-size: 12px;
                    line-height: 1;
                }
                .events-input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }
                .events-field-icon {
                    position: absolute;
                    right: 12px;
                    color: #10b981;
                    font-size: 14px;
                    z-index: 1;
                }
                .events-modal-input {
                    width: 100%;
                    height: 40px;
                    background: #FFFFFF;
                    border: 1px solid #e0e2e6;
                    border-radius: 8px;
                    padding: 0 14px 0 12px;
                    color: #181d26;
                    font-family: 'Cairo', sans-serif;
                    font-size: 13px;
                    font-weight: 400;
                    transition: 0.2s;
                    outline: none;
                    box-sizing: border-box;
                    direction: rtl;
                }
                .events-modal-input::placeholder {
                    color: #9ca3af;
                    font-weight: 400;
                }
                .events-input-wrapper:not(:has(.events-date-input)) .events-modal-input {
                    padding: 0 38px 0 12px;
                }
                .events-date-input {
                    font-family: Arial, sans-serif;
                    direction: ltr;
                    text-align: center;
                    padding: 0 10px;
                }
                .events-modal-input:focus {
                    border-color: #10b981;
                    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
                    background: #FFFFFF;
                }

                .events-modal-footer {
                    padding: 12px 24px;
                    background: #FFFFFF;
                    border-top: 1px solid #f1f5f9;
                    display: flex;
                    justify-content: flex-end;
                    gap: 10px;
                }
                .events-modal-submit {
                    min-width: 120px;
                    background: #10b981;
                    border: none;
                    color: white;
                    height: 38px;
                    padding: 0 16px;
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 400;
                    cursor: pointer;
                    transition: 0.2s;
                    font-family: 'Cairo', sans-serif;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    white-space: nowrap;
                }
                .events-modal-submit:hover {
                    background: #059669;
                    transform: translateY(-1px);
                }

                @keyframes spin { to { transform: rotate(360deg); } }

                .confirm-toast { text-align: center; font-family: 'Cairo', sans-serif; }
                .toast-actions { display: flex; justify-content: center; gap: 12px; margin-top: 20px; }
                .btn-confirm { border: none; padding: 10px 22px; border-radius: 12px; color: white; cursor: pointer; font-weight: 400; font-family: 'Cairo', sans-serif; }
                .btn-cancel { background: var(--panel-2); color: var(--text); border: 1px solid var(--border); padding: 10px 22px; border-radius: 12px; cursor: pointer; font-family: 'Cairo', sans-serif; font-weight: 400; }

                @media (max-width: 1000px) {
                    .header-tools, .header-buttons-group { flex-direction: column; align-items: stretch; width: 100%; gap: 10px; }
                    .global-search-container, .results-count { display: none !important; }
                    .bulk-delete-btn { position: relative; left: 0; top: 0; width: 100%; justify-content: center; margin-bottom: 10px; }
                    .bulk-delete-btn.show { top: 0; margin-bottom: 10px; }
                    .premium-action-btn { width: 100%; justify-content: center; }
                    .holidays-modern-grid { grid-template-columns: 1fr; }
                    .stats-row { grid-template-columns: repeat(2, 1fr); }
                }

                @media (max-width: 600px) {
                    .stats-row { grid-template-columns: 1fr; }
                }
            `}</style>
    </div>
  );
};

export default AnnualEvents;
